import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFormHook from '@shared/hooks/useFormHook';
import { positionsService } from '@modules/organizationalStructure/positions/services/positions';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import LoadingButton from '@shared/components/LoadingButton';

export default function PositionTranslations({ onClose, position, onSuccess }) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { register, handleSubmit, setValue } = useForm();

    useEffect(() => {
        const fetchTranslations = async () => {
            if (position) {
                try {
                    setLoading(true);
                    setError(null);
                    const translationData = await positionsService.getTranslation(position.id);
                    
                    // Set original values
                    setValue('abbreviation', position.shortDescription);
                    setValue('title', position.longDescription);

                    // Set translated values if they exist
                    if (translationData && translationData.length > 0) {
                        const translation = translationData[0]; // Get first translation
                        setValue('abbreviationTranslated', translation.shortDescription);
                        setValue('titleTranslated', translation.longDescription);
                    } else {
                        // If no translations exist, set default values
                        setValue('abbreviationTranslated', '');
                        setValue('titleTranslated', '');
                    }
                } catch (err) {
                    console.error('Error fetching translations:', err);
                    setError('Failed to load translations');
                    notifyError(t('common.errorMessage'));
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchTranslations();
    }, [position, setValue]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            const translationData = {
                jobId: position.id,
                shortDescription: data.abbreviationTranslated,
                longDescription: data.titleTranslated
            };

            await positionsService.saveTranslation(translationData);
            notifySuccess(t('common.updatedSuccessfully'));
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error saving translation:', error);
            setError('Failed to save translation');
            notifyError(t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="modal show" tabIndex="-1">
                <div className="modal-dialog modal-confirm" ref={modalRef}>
                    <div className="modal-content">
                        <div className="modal-body">
                            <p>Loading translations...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {t('organizationalStructure.positions.table.translationsForm.translations')}
                        </h4>
                        <form id="translationsForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.positions.table.headers.abbreviation')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('abbreviation')}
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.positions.table.headers.title')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('title')}
                                            disabled
                                        />
                                    </div>
                                </div>

                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.positions.table.translationsForm.abbreviationTranslated')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('abbreviationTranslated', { required: true })}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.positions.table.translationsForm.titleTranslated')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('titleTranslated', { required: true })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        {/* <button type="submit" form="translationsForm" className="btn btn-primary">
                            {t('common.save')}
                        </button> */}
                        <LoadingButton type="submit" form="translationsForm" className="btn btn-primary" loading={loading} disabled={loading}>{t('common.save')}</LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}